// Browser stand-in for node:fs/promises.
//
// Operations are tried against the FSA mount engine first (directory
// mounts picked via the File System Access API plus the session write
// overlay — see fsa-core.cjs). Paths outside that world keep the baseline
// policy: reads reject ENOENT, listings resolve empty, writes resolve.
const core = require("./fsa-core.cjs");

function enoent(filePath) {
    const err = new Error(
        `ENOENT: no such file or directory, web build has no filesystem '${String(filePath)}'`,
    );
    err.code = "ENOENT";
    err.errno = -2;
    err.path = String(filePath);
    return err;
}

const reject = (filePath) => Promise.reject(enoent(filePath));

// The renderer reads static assets (e.g. svg icon sets) from the install
// directory via fs; in the web build those files are served over HTTP under
// assets/, so reads below the fake assets path translate to fetch().
const ASSETS_PREFIX = "/vortex/assets/";

function tryFetchAsset(filePath) {
    const normalized = String(filePath).replace(/\\/g, "/");
    if (!normalized.startsWith(ASSETS_PREFIX)) {
        return null;
    }
    return fetch("assets/" + normalized.slice(ASSETS_PREFIX.length)).then(
        (res) => {
            if (!res.ok) {
                throw enoent(filePath);
            }
            return res.text();
        },
    );
}

// wraps a core op: handled results pass through, NOT_HANDLED falls back
function viaCore(name, fallback) {
    return async function (p, ...rest) {
        const result = await core[name](p, ...rest);
        if (result !== core.NOT_HANDLED) {
            return result;
        }
        return fallback(p, ...rest);
    };
}

module.exports = {
    readFile: viaCore("readFile", (p) => tryFetchAsset(p) ?? reject(p)),
    stat: viaCore("stat", (p) => reject(p)),
    lstat: viaCore("stat", (p) => reject(p)),
    access: viaCore("access", (p) => reject(p)),
    readdir: viaCore("readdir", () => []),

    open: (p) => reject(p),
    realpath: (p) =>
        // our virtual paths (mounts, staging, picked files) are already
        // canonical; resolve to the normalized form if the path exists
        core
            .stat(p)
            .then((result) =>
                result === core.NOT_HANDLED ? reject(p) : core.normalize(p),
            )
            .catch(() => reject(p)),
    readlink: (p) => reject(p),
    opendir: (p) => reject(p),

    writeFile: (p, data, options) => core.writeFile(p, data, options),
    appendFile: async (p, data, options) => {
        const existing = await core.readFile(p).catch(() => null);
        const prev =
            existing !== null && existing !== core.NOT_HANDLED
                ? existing
                : new Uint8Array();
        const next = typeof data === "string" ? data : new Uint8Array(data);
        const merged = new Uint8Array(prev.length + next.length);
        merged.set(prev instanceof Uint8Array ? prev : new Uint8Array(prev), 0);
        merged.set(
            typeof next === "string" ? new TextEncoder().encode(next) : next,
            prev.length,
        );
        return core.writeFile(p, merged, options);
    },
    mkdir: (p, options) => core.mkdir(p, options),
    rmdir: (p, options) => core.rm(p, options),
    rm: (p, options) => core.rm(p, options),
    unlink: (p) => core.unlink(p),
    rename: (from, to) => core.rename(from, to),
    copyFile: async (from, to) => {
        const data = await core.readFile(from).catch(() => null);
        if (data !== null && data !== core.NOT_HANDLED) {
            return core.writeFile(to, data);
        }
        return undefined; // pretend success, matches baseline write policy
    },
    chmod: () => Promise.resolve(),
    utimes: () => Promise.resolve(),
    symlink: () => Promise.resolve(),
    // hardlinks don't exist in the browser fs. Reject EXDEV so callers that
    // try linkAsync-then-copyAsync (mod staging) fall back to a real copy.
    link: (from) => {
        const err = new Error(
            `EXDEV: cross-device link not permitted, link '${String(from)}'`,
        );
        err.code = "EXDEV";
        err.errno = -18;
        return Promise.reject(err);
    },
};
